import { mount } from '../dom-lifecycle';
import { DateField } from './date-field';
import { renderPagination } from './pagination';
import { createRankingTable, paginationState, renderBody, renderHead, setTableData } from './table';

/**
 * Strona rankingu: dwie zakładki (dzienny / generalny), wybór dnia, wyszukiwanie po numerze
 * telefonu lub adresie e-mail, rozmiar strony i paginacja.
 *
 * Podział stanu: paginację (`pageIndex`, `pageSize`) trzyma instancja TanStack Table — to
 * z niej liczy się liczba stron i podsumowanie — a rodzaj rankingu, dzień i frazę trzyma
 * `state` niżej. Każda zmiana przechodzi przez `load()`, więc nie ma dwóch dróg do tego
 * samego odczytu i nie trzeba pilnować pętli render → fetch → render.
 *
 * Odpowiedzi z API bywają spóźnione (szybkie klikanie po stronach), dlatego każde żądanie
 * dostaje numer i przerywa poprzednie: liczy się wyłącznie ostatnie.
 */

const TYPE_DAILY = 'daily';

const numberFormat = new Intl.NumberFormat('pl-PL');

class Ranking {
    constructor(root) {
        this.root = root;
        this.endpoints = {
            [TYPE_DAILY]: root.dataset.rankingEndpointDaily,
            global: root.dataset.rankingEndpointGlobal,
        };

        this.state = {
            type: root.dataset.rankingType === 'global' ? 'global' : TYPE_DAILY,
            date: root.dataset.rankingDate || null,
            query: root.dataset.rankingQuery || '',
        };

        this.el = {
            tabs: [...root.querySelectorAll('[data-ranking-tab]')],
            days: root.querySelector('[data-ranking-days]'),
            dateInput: root.querySelector('[data-ranking-date-input]'),
            dayPrev: root.querySelector('[data-ranking-day-prev]'),
            dayNext: root.querySelector('[data-ranking-day-next]'),
            search: root.querySelector('[data-ranking-search]'),
            searchInput: root.querySelector('[data-ranking-search-input]'),
            notice: root.querySelector('[data-ranking-notice]'),
            head: root.querySelector('[data-ranking-head]'),
            body: root.querySelector('[data-ranking-body]'),
            message: root.querySelector('[data-ranking-message]'),
            loader: root.querySelector('[data-ranking-loader]'),
            summary: root.querySelector('[data-ranking-summary]'),
            perPage: root.querySelector('[data-ranking-per-page-select]'),
            perPageWrap: root.querySelector('.ranking__per-page'),
            pagination: root.querySelector('[data-ranking-pagination]'),
        };

        this.perPageDefault = Number(root.dataset.rankingPerPageDefault);

        this.table = createRankingTable({
            pageIndex: Math.max(0, Number(root.dataset.rankingPage || 1) - 1),
            pageSize: Number(this.el.perPage.value),
        });

        this.dateField = new DateField(this.el.dateInput, (date) => this.filter({ date }));
        this.requestId = 0;
        this.pending = null;
        // Żądanie strony poza zakresem poprawiamy raz — inaczej uparte 404/pustki mogłyby
        // wozić nas w kółko między stroną spoza zakresu i ostatnią istniejącą.
        this.clamped = false;
    }

    init() {
        renderHead(this.table, this.el.head);
        this.bind();
        this.syncTabs();
        this.load();
    }

    bind() {
        this.el.tabs.forEach((tab) => {
            tab.addEventListener('click', () => this.switchType(tab.dataset.rankingTab));
        });

        this.el.dayPrev.addEventListener('click', () => this.moveDay(-1));
        this.el.dayNext.addEventListener('click', () => this.moveDay(1));

        this.el.search.addEventListener('submit', (event) => {
            event.preventDefault();
            this.filter({ query: this.el.searchInput.value.trim() });
        });

        // Wyczyszczenie pola (krzyżyk w polu type=search) wraca do pełnej listy
        this.el.searchInput.addEventListener('search', () => {
            if (this.el.searchInput.value.trim() === '') {
                this.filter({ query: '' });
            }
        });

        this.el.perPage.addEventListener('change', () => {
            this.table.setPagination({ pageIndex: 0, pageSize: Number(this.el.perPage.value) });
            this.load();
        });
    }

    switchType(type) {
        if (type === this.state.type) {
            return;
        }

        this.state.type = type;
        this.syncTabs();
        this.filter({});
    }

    /** Zmiana filtra zawsze cofa na pierwszą stronę — na trzeciej mogłoby już nic nie być. */
    filter(changes) {
        Object.assign(this.state, changes);
        this.table.setPageIndex(0);
        this.load();
    }

    moveDay(step) {
        const date = this.dateField.neighbour(step);

        if (date) {
            this.dateField.select(date);
            this.filter({ date });
        }
    }

    goToPage(pageIndex) {
        this.table.setPageIndex(pageIndex);
        this.load();
        this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    async load() {
        const requestId = ++this.requestId;
        this.pending?.abort();
        this.pending = new AbortController();
        this.el.loader.hidden = false;

        try {
            const response = await fetch(this.url(), {
                headers: { Accept: 'application/json' },
                signal: this.pending.signal,
            });
            const payload = await response.json();

            // Wyścig: zdążyło wyjść nowsze żądanie, więc ta odpowiedź jest już nieaktualna
            if (requestId !== this.requestId) {
                return;
            }

            if (!response.ok) {
                this.fail(payload.error === 'no_such_day'
                    ? 'Dla wybranego dnia nie ma rankingu.'
                    : 'Nie udało się wczytać rankingu. Odśwież stronę.');

                return;
            }

            this.apply(payload);
        } catch (error) {
            if (error.name === 'AbortError' || requestId !== this.requestId) {
                return;
            }

            this.fail('Nie udało się wczytać rankingu. Sprawdź połączenie i spróbuj ponownie.');
        } finally {
            if (requestId === this.requestId) {
                this.el.loader.hidden = true;
            }
        }
    }

    url() {
        const { pageIndex, pageSize } = paginationState(this.table);
        const url = new URL(this.endpoints[this.state.type], window.location.origin);

        url.searchParams.set('page', String(pageIndex + 1));
        url.searchParams.set('perPage', String(pageSize));

        if (this.state.query) {
            url.searchParams.set('q', this.state.query);
        }

        if (this.state.type === TYPE_DAILY && this.state.date) {
            url.searchParams.set('date', this.state.date);
        }

        return url;
    }

    apply(payload) {
        // Dzień ustala backend (bez `date` — najbliższy istniejący), więc stan bierzemy z
        // odpowiedzi, a nie z tego, o co pytaliśmy.
        if (payload.day) {
            this.state.date = payload.day.date;
            this.dateField.setDays(payload.days ?? [], payload.day.date);
        }

        setTableData(this.table, payload.data, payload.meta.total);

        // Strona spoza zakresu (np. z linku) — cofamy na ostatnią istniejącą
        if (!payload.search && payload.meta.page > payload.meta.lastPage && !this.clamped) {
            this.clamped = true;
            this.table.setPageIndex(payload.meta.lastPage - 1);
            this.load();

            return;
        }

        this.clamped = false;
        this.render(payload);
    }

    render(payload) {
        renderBody(this.table, this.el.body);

        const empty = payload.data.length === 0;
        this.el.message.hidden = !empty;
        this.el.message.textContent = empty ? this.emptyMessage(payload) : '';

        this.renderNotice(payload);
        this.renderSummary(payload);

        // Przy wyszukiwaniu API oddaje najwyżej jeden wiersz — stronicowanie i rozmiar
        // strony nie mają wtedy czego dzielić.
        this.el.perPageWrap.hidden = payload.search;

        if (payload.search) {
            this.el.pagination.replaceChildren();
        } else {
            renderPagination(this.table, this.el.pagination, (pageIndex) => this.goToPage(pageIndex));
        }

        this.syncDayNav();
        this.syncUrl();
    }

    emptyMessage(payload) {
        return payload.search
            ? 'Nie znaleźliśmy takiego uczestnika. Wyszukiwanie wymaga pełnego numeru telefonu '
                + 'albo dokładnego adresu e-mail.'
            : 'W tej klasyfikacji nikt jeszcze nie rozegrał gry.';
    }

    renderNotice(payload) {
        const parts = [];

        if (payload.search) {
            parts.push(`Wyniki dla: ${this.state.query}`);
        }

        this.el.notice.hidden = parts.length === 0;
        this.el.notice.textContent = parts.join(' ');

        if (payload.search) {
            const clear = document.createElement('button');
            clear.type = 'button';
            clear.className = 'btn btn-outline ranking__notice-clear';
            clear.textContent = 'Wyczyść';
            clear.addEventListener('click', () => {
                this.el.searchInput.value = '';
                this.filter({ query: '' });
            });
            this.el.notice.append(clear);
        }
    }

    renderSummary(payload) {
        if (payload.search || payload.meta.total === 0) {
            this.el.summary.textContent = '';

            return;
        }

        const { pageIndex, pageSize } = paginationState(this.table);
        const from = pageIndex * pageSize + 1;
        const to = Math.min(from + payload.data.length - 1, payload.meta.total);

        this.el.summary.textContent = `Pozycja od ${numberFormat.format(from)} `
            + `do ${numberFormat.format(to)} z ${numberFormat.format(payload.meta.total)} łącznie`;
    }

    syncTabs() {
        this.el.tabs.forEach((tab) => {
            const active = tab.dataset.rankingTab === this.state.type;
            tab.classList.toggle('is-active', active);
            tab.classList.toggle('btn-primary', active);
            tab.classList.toggle('btn-outline', !active);
            tab.setAttribute('aria-selected', String(active));
        });

        // Ranking generalny nie ma dnia — wybór dnia znika razem z zakładką
        this.el.days.hidden = this.state.type !== TYPE_DAILY;
    }

    syncDayNav() {
        this.el.dayPrev.disabled = this.dateField.neighbour(-1) === null;
        this.el.dayNext.disabled = this.dateField.neighbour(1) === null;
    }

    /** Adres odzwierciedla to, co widać — link do konkretnej strony rankingu ma działać. */
    syncUrl() {
        const { pageIndex, pageSize } = paginationState(this.table);
        const url = new URL(window.location.href);
        const params = url.searchParams;

        set(params, 'typ', this.state.type === TYPE_DAILY ? '' : this.state.type);
        set(params, 'data', this.state.type === TYPE_DAILY ? this.state.date : '');
        set(params, 'q', this.state.query);
        set(params, 'strona', pageIndex === 0 ? '' : String(pageIndex + 1));
        set(params, 'na-strone', pageSize === this.perPageDefault ? '' : String(pageSize));

        const query = params.toString();
        window.history.replaceState(null, '', `${url.pathname}${query ? `?${query}` : ''}`);
    }

    fail(message) {
        setTableData(this.table, [], 0);
        renderBody(this.table, this.el.body);
        this.el.pagination.replaceChildren();
        this.el.summary.textContent = '';
        this.el.notice.hidden = true;
        this.el.message.hidden = false;
        this.el.message.textContent = message;
    }
}

// Montaż na końcu pliku: mount() woła callback od razu dla elementów już obecnych
// w DOM, więc klasa musi być w tym momencie zdefiniowana.
mount('[data-ranking]', function () {
    new Ranking(this).init();
});

/** Puste wartości znikają z adresu, żeby link był krótki i czytelny. */
function set(params, key, value) {
    if (value) {
        params.set(key, value);
    } else {
        params.delete(key);
    }
}
